import type { ReactNode } from 'react';

// Los íconos del layout del admin: el menú lateral, el app bar y la barra
// derecha. Son los trazos del Figma («Menu - Select») llevados a una caja de
// 24, con el blanco cambiado por `currentColor` para que hereden el color del
// texto. El tamaño en pantalla es el del Figma: 24 en el menú, 26 el usuario y
// 31 los de la barra derecha.
//
// Van aparte de `iconos.tsx` porque son rellenos grandes, no trazos de una
// línea, y mezclarlos taparía los otros. Todos llevan `aria-hidden`: acompañan
// a un texto o a un `aria-label`.

function Svg({ tamano = 24, vista = '0 0 24 24', children }: { tamano?: number; vista?: string; children: ReactNode }) {
  return (
    <svg width={tamano} height={tamano} viewBox={vista} fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

export function IconoDashboard() {
  return (
    <Svg>
      <path
        d="M8.4 20.4H7.2a3.6 3.6 0 0 1-3.6-3.6v-1.2h4.8v4.8Zm12-3.6a3.6 3.6 0 0 1-3.6 3.6H9.6v-4.8h10.8v1.2Zm-6-2.4H3.6V7.2a3.6 3.6 0 0 1 3.6-3.6h7.2v10.8Zm6 0h-4.8V9.6h4.8v4.8ZM16.8 3.6a3.6 3.6 0 0 1 3.6 3.6v1.2h-4.8V3.6h1.2Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoCaja() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1.61 21 6.806v10.393l-9 5.196-9-5.196V6.806l9-5.196ZM9 11.309v7.044l2 1.155v-7.044L9 11.31ZM5 9v7.044l2 1.155v-7.044L5 9Zm10.937-2.808-5.886 3.415L12 10.732l5.9-3.406-1.963-1.134ZM12 3.92 6.1 7.326l1.953 1.127 5.886-3.414L12 3.92Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/**
 * Los activos (#119): un carro visto de frente. No está en el Figma —el menú del
 * diseño no tiene destino de activos—, así que sigue el estilo de los demás:
 * relleno, en una caja de 24 y con `currentColor`.
 */
export function IconoVehiculo() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.6 4h10.8l2.25 6H21a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-2H8v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h1.35L6.6 4Zm1.4 2-1.5 4h11l-1.5-4H8Zm-1.5 6.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm11 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** La sucursal: el pin de ubicación. Acompaña al selector del app bar (#101). */
/**
 * El taller, para el selector del app bar (#205). El Figma no lo tiene —el
 * selector nació después—: es el frente de un local con su portón, en el mismo
 * relleno que el pin de la sucursal para que los dos se lean como pareja.
 */
export function IconoTaller() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.5 2 7.5V21h20V7.5l-10-5ZM6 11h12v10h-2v-8H8v8H6V11Zm4 4h4v1.5h-4V15Zm0 3h4v1.5h-4V18Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoSucursal() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoPersona() {
  return (
    <Svg>
      <path
        d="M6.75 7.5a5.25 5.25 0 1 0 10.5 0 5.25 5.25 0 0 0-10.5 0ZM19.5 21.75H3.75A.75.75 0 0 1 3 21v-2.25A3.75 3.75 0 0 1 6.75 15h10.5A3.75 3.75 0 0 1 21 18.75V21a.75.75 0 0 1-.75.75h-.75Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoGafete() {
  return (
    <Svg>
      <path
        d="M16.478 3.6h.322a2.4 2.4 0 0 1 2.4 2.4v13.2a2.4 2.4 0 0 1-2.4 2.4H7.2a2.4 2.4 0 0 1-2.4-2.4V6a2.4 2.4 0 0 1 2.4-2.4h.322A2.4 2.4 0 0 1 9.6 2.4h4.8c.889 0 1.665.484 2.078 1.2ZM14.1 6.6a.9.9 0 0 0 0-1.8H9.9a.9.9 0 0 0 0 1.8h4.2ZM12 15.3a2.1 2.1 0 1 0 0-4.2 2.1 2.1 0 0 0 0 4.2Zm-3.514 2.588c-.484.584.083 1.312.84 1.312h5.344c.757 0 1.324-.728.84-1.313a3.003 3.003 0 0 0-2.314-1.087h-2.4c-.93 0-1.762.424-2.314 1.088h.004Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoPortapapeles() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 2a2 2 0 0 0-2 2H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1a2 2 0 0 0-2-2H9Zm0 2h6v2H9V4Zm-1 7h8v2H8v-2Zm0 4h5v2H8v-2Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** Gastos (#144). No está en el Figma: un recibo con el borde de abajo dentado, del trazo de los demás. */
export function IconoRecibo() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 2a2 2 0 0 0-2 2v17.2a.8.8 0 0 0 1.25.66L7 20.6l1.75 1.26a.8.8 0 0 0 .94 0L11.44 20.6l1.75 1.26a.8.8 0 0 0 .94 0l1.75-1.26 1.75 1.26a.8.8 0 0 0 1.25-.66V4a2 2 0 0 0-2-2H6Zm2 5h8v2H8V7Zm0 4h8v2H8v-2Zm0 4h5v2H8v-2Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoAgenda() {
  return (
    <Svg>
      <path
        d="M17.571 12a5.571 5.571 0 1 1 0 11.143 5.571 5.571 0 0 1 0-11.143Zm1.072-9.429a2.786 2.786 0 0 1 2.786 2.786v7.072a6.43 6.43 0 0 0-5.506-1.072H7.714l-.087.006a.643.643 0 0 0 0 1.274l.087.006h5.732a6.43 6.43 0 0 0-2.07 3.214H7.714l-.087.006a.643.643 0 0 0 0 1.274l.087.006h3.445a6.43 6.43 0 0 0 1.27 4.286H5.357a2.786 2.786 0 0 1-2.786-2.786V5.357A2.786 2.786 0 0 1 5.357 2.57h13.286Zm-1.072 11.572a.429.429 0 0 0-.428.428v3c0 .237.192.429.428.429h2.572a.429.429 0 0 0 0-.857H18v-2.572a.429.429 0 0 0-.429-.428ZM7.627 6.863a.643.643 0 0 0 0 1.274l.087.006h8.572l.087-.006a.643.643 0 0 0 0-1.274l-.087-.006H7.714l-.087.006Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoReporte() {
  return (
    <Svg>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.25 2.5a.25.25 0 0 0-.25-.25H7A2.75 2.75 0 0 0 4.25 5v14A2.75 2.75 0 0 0 7 21.75h10A2.75 2.75 0 0 0 19.75 19V9.147a.25.25 0 0 0-.25-.25H15a.75.75 0 0 1-.75-.75V2.5ZM15 12.25a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1 0-1.5h6Zm0 4a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1 0-1.5h6Z"
        fill="currentColor"
      />
      <path
        d="M15.75 2.824c0-.184.193-.301.336-.186.121.098.229.212.323.342l3.013 4.197c.068.096-.006.22-.124.22H16a.25.25 0 0 1-.25-.25V2.824Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoEngrane() {
  return (
    <Svg>
      <path
        d="M12.012 9.155c-.701 0-1.357.272-1.854.768a2.608 2.608 0 0 0-.769 1.854c0 .701.275 1.357.769 1.854.497.495 1.153.769 1.854.769.7 0 1.357-.274 1.854-.769.494-.497.768-1.153.768-1.854 0-.7-.274-1.357-.768-1.854a2.603 2.603 0 0 0-1.854-.768Zm9.663 5.52-1.533-1.311c.073-.445.11-.9.11-1.352 0-.453-.037-.91-.11-1.353l1.533-1.31a.75.75 0 0 0 .218-.825l-.021-.061a10.37 10.37 0 0 0-1.866-3.227l-.042-.05a.752.752 0 0 0-.823-.222l-1.903.677a8.172 8.172 0 0 0-2.334-1.348l-.368-1.99a.75.75 0 0 0-.605-.602l-.063-.011a10.56 10.56 0 0 0-3.722 0l-.063.011a.75.75 0 0 0-.605.602l-.37 2a8.25 8.25 0 0 0-2.318 1.343l-1.917-.682a.75.75 0 0 0-.823.223l-.042.049a10.4 10.4 0 0 0-1.866 3.227l-.021.061a.752.752 0 0 0 .218.826l1.552 1.324a8.21 8.21 0 0 0 0 2.672l-1.547 1.324a.75.75 0 0 0-.218.825l.021.061c.424 1.18 1.05 2.269 1.866 3.228l.042.049a.752.752 0 0 0 .823.222l1.917-.682c.698.574 1.477 1.029 2.318 1.343l.37 2a.75.75 0 0 0 .605.602l.063.011c1.231.222 2.491.222 3.722 0l.063-.011a.75.75 0 0 0 .605-.602l.368-1.99a8.2 8.2 0 0 0 2.334-1.348l1.903.678a.75.75 0 0 0 .823-.223l.042-.049a10.4 10.4 0 0 0 1.866-3.227l.021-.061a.75.75 0 0 0-.223-.82Zm-9.663 1.223a4.12 4.12 0 1 1 0-8.241 4.12 4.12 0 0 1 0 8.241Z"
        fill="currentColor"
      />
    </Svg>
  );
}

export function IconoSalir() {
  return (
    <Svg>
      <path
        d="M20 12h-9.5M18 15l3-3-3-3M13 7V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5a2 2 0 0 0 2-2v-1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** La persona en un círculo del app bar («boxicons:user-circle»). */
export function IconoUsuario() {
  return (
    <Svg tamano={26}>
      <path
        d="M12 6c-2.28 0-4 1.72-4 4s1.72 4 4 4 4-1.72 4-4-1.72-4-4-4Zm0 6c-1.18 0-2-.82-2-2s.82-2 2-2 2 .82 2 2-.82 2-2 2Z"
        fill="currentColor"
      />
      <path
        d="M12 2C6.49 2 2 6.49 2 12c0 3.26 1.58 6.16 4 7.98V20h.03A9.94 9.94 0 0 0 12 22c2.24 0 4.31-.75 5.97-2H18v-.02c2.42-1.83 4-4.72 4-7.98 0-5.51-4.49-10-10-10ZM8.18 19.02A3.01 3.01 0 0 1 11 17h2c1.31 0 2.42.85 2.82 2.02A7.9 7.9 0 0 1 12 20a7.93 7.93 0 0 1-3.82-.98Zm9.3-1.21A4.99 4.99 0 0 0 13 15h-2a5 5 0 0 0-4.48 2.81A7.97 7.97 0 0 1 4 11.99C4 7.58 7.59 3.99 12 3.99s8 3.59 8 8c0 2.29-.97 4.36-2.52 5.82Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** «fluent:person-support-24-filled» de la barra derecha. */
export function IconoSoporte() {
  return (
    <Svg tamano={31}>
      <path
        d="M10.155 14.773l-.009-.021a7.2 7.2 0 0 1-.4-.123l-.01-.004A7.013 7.013 0 0 1 5 8a7 7 0 0 1 13.96-.75c.044.411-.296.75-.71.75-.414 0-.745-.338-.8-.748a5.502 5.502 0 1 0-7.24 5.912 1.998 1.998 0 0 1 3.79.867 2 2 0 0 1-3.845.742Zm-1.025 1.23A8.47 8.47 0 0 1 5.994 14a2.25 2.25 0 0 0-1.994 2.236v.92c0 .572.178 1.13.51 1.596C6.052 20.916 8.576 21.987 12 21.987s5.95-1.072 7.495-3.235A2.75 2.75 0 0 0 20 17.157v-.918A2.25 2.25 0 0 0 17.75 14H15.5a3.5 3.5 0 0 1-6.37 2.003ZM16 8a4 4 0 1 0-6.244 3.311A3.48 3.48 0 0 1 12 10.5c.853 0 1.635.305 2.243.813A3.996 3.996 0 0 0 16 8Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** «akar-icons:wifi» de la barra derecha. */
export function IconoWifi() {
  return (
    <Svg tamano={31}>
      <path
        d="M2 8.94c5.5-6.11 14.5-6.11 20 0M5.67 12.6c3.3-3.67 9.36-3.67 12.66 0"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="17.6" r="1" stroke="currentColor" strokeWidth="1.6" />
    </Svg>
  );
}

/** «akar-icons:sun-fill» de la barra derecha. */
export function IconoSol() {
  return (
    <Svg tamano={31}>
      <path d="M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z" fill="currentColor" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1ZM3.293 3.293a1 1 0 0 1 1.414 0l1.5 1.5a1 1 0 0 1-1.414 1.414l-1.5-1.5a1 1 0 0 1 0-1.414Zm17.414 0a1 1 0 0 1 0 1.414l-1.5 1.5a1 1 0 1 1-1.414-1.414l1.5-1.5a1 1 0 0 1 1.414 0ZM1 12a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H2a1 1 0 0 1-1-1Zm19 0a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2h-1a1 1 0 0 1-1-1ZM6.207 17.793a1 1 0 0 1 0 1.414l-1.5 1.5a1 1 0 0 1-1.414-1.414l1.5-1.5a1 1 0 0 1 1.414 0Zm11.586 0a1 1 0 0 1 1.414 0l1.5 1.5a1 1 0 0 1-1.414 1.414l-1.5-1.5a1 1 0 0 1 0-1.414ZM12 20a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Z"
        fill="currentColor"
      />
    </Svg>
  );
}
